const CAPACITY = 5;
/** Derive only visible, unsettled physical gates. Renderer quality and redraws
 * never change contact, speed, rewards or route completion in the domain. */
export function skyVisibleCourseGates(state) {
  const course = state?.flightCourse;
  if (
    !course ||
    course.profile?.pattern === 'resonance' ||
    course.sector !== state.sector ||
    state.boss ||
    state.traversal ||
    state.leviathan ||
    state.cathedral
  )
    return [];
  return course.gates
    .filter((gate) => gate.status === 'ahead' && gate.z > -145 && gate.z < 5)
    .slice(0, CAPACITY);
}

/** Three fixed-capacity batches show open physical apertures, next-gate arrows
 * and route breadcrumbs. Geometry stays outside the1.65m legal gate opening.
 * The renderer owns/disposes every resource through own(); no textures, lights,
 * per-frame meshes or autonomous clocks are allocated by this presentation. */
export function createSkyFlightCourseArt(T, scene, own) {
  const root = new T.Group();
  root.name = 'Sky physical flight courses';
  root.visible = false;
  scene.add(root);
  const ringGeometry = own(new T.TorusGeometry(1.725, 0.075, 6, 48));
  const material = own(
    // Instance colour tints this unlit surface directly. A white emissive term
    // bypasses instance colour and blows the navigation palette into white.
    new T.MeshBasicMaterial({ color: '#ffffff', toneMapped: false }),
  );
  const rings = own(new T.InstancedMesh(ringGeometry, material, CAPACITY));
  const arrowGeometry = own(new T.BoxGeometry(0.5, 0.1, 0.08));
  const arrows = own(new T.InstancedMesh(arrowGeometry, material, CAPACITY * 2));
  const dotGeometry = own(new T.OctahedronGeometry(0.09));
  const dots = own(new T.InstancedMesh(dotGeometry, material, CAPACITY * 5));
  for (const mesh of [rings, arrows, dots]) {
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);
    root.add(mesh);
  }
  const matrix = new T.Object3D();
  const gold = new T.Color('#ffd184'),
    cyan = new T.Color('#72d9ed'),
    white = new T.Color('#fff3d3');
  let visible = 0;
  function place(mesh, index, x, y, z, roll = 0, scale = 1) {
    matrix.position.set(x, y, z);
    matrix.rotation.set(0, 0, roll);
    matrix.scale.setScalar(scale);
    matrix.updateMatrix();
    mesh.setMatrixAt(index, matrix.matrix);
  }
  function draw(state, enabled = true) {
    const gates = enabled ? skyVisibleCourseGates(state) : [];
    visible = gates.length;
    root.visible = visible > 0;
    if (!root.visible) return;
    rings.count = gates.length;
    arrows.count = gates.length * 2;
    let dotCount = 0;
    for (const [index, gate] of gates.entries()) {
      place(rings, index, gate.x, gate.y, gate.z, 0, gate.radius / 1.65);
      rings.setColorAt(index, index === 0 ? gold : cyan);
      for (const side of [-1, 1]) {
        const slot = index * 2 + (side === 1 ? 1 : 0);
        place(
          arrows,
          slot,
          gate.x + side * 0.18,
          gate.y + gate.radius + 0.58,
          gate.z,
          (side * Math.PI) / 4,
        );
        arrows.setColorAt(slot, index === 0 ? white : cyan);
      }
      const next = gates[index + 1];
      if (next)
        for (let step = 1; step <= 5; step++) {
          const fraction = step / 6;
          place(
            dots,
            dotCount,
            gate.x + (next.x - gate.x) * fraction,
            gate.y + (next.y - gate.y) * fraction,
            gate.z + (next.z - gate.z) * fraction,
          );
          dots.setColorAt(dotCount++, cyan);
        }
    }
    dots.count = dotCount;
    for (const mesh of [rings, arrows, dots]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  }
  return { draw, diagnostics: () => ({ visibleGates: visible, capacity: CAPACITY, batches: 3 }) };
}
